import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import { ToastContainer, toast } from 'react-toastify';
import { useUser } from '../../utils/userContext';
import useHandleErrors from '../../utils/user.utils';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';

const ViewTimesheet = () => {
    const [timesheets, setTimesheets] = useState([]);
    const { user } = useUser();
    const [filter, setFilter] = useState('currentMonth');
    const [employeeId, setEmployeeId] = useState("");
    const [employeeName, setEmployeeName] = useState("");
    const handleErrors = useHandleErrors();
    const [loading, setLoading] = useState(false);
    const hasFetchedRef = useRef(false);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [selectedTimesheet, setSelectedTimesheet] = useState(null);

    useEffect(() => {
        const fetchTimesheetDetails = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("token");
                const path = `/viewTimesheet?employeeId=${user?.EmployeeId}`;
                const URL = process.env.REACT_APP_BE_URL + path;
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.status === 200) {
                    const data = response.data.listTimesheetDetails.flatMap(emp => emp.timesheetSummary);
                    const employeeId = response.data.listTimesheetDetails[0].employeeId;
                    const employeeName = response.data.listTimesheetDetails[0].employeeName;
                    setEmployeeId(employeeId);
                    setEmployeeName(employeeName);
                    setTimesheets(data);
                } else {
                    handleErrors(response);
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 404) {
                        // Handle 404 status code separately
                        setEmployeeId([]);
                        setTimesheets([]);
                    } else {
                        handleErrors(error.response);
                    }
                } else {
                    toast.error("Error submitting timesheet.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            } finally {
                setLoading(false);
            }
        };

        if (!hasFetchedRef.current) {
            fetchTimesheetDetails();
            hasFetchedRef.current = true;
        }
    }, [user, handleErrors]);

    useEffect(() => {
        const preloading = document.getElementById("preloading");
        setTimeout(() => {
            if (preloading) {
                preloading.style.display = "none";
            }
        }, 400);
    }, []);

    const handleFilterChange = (e) => {
        setFilter(e.target.id);
    };

    const getFilteredTimesheets = () => {
        return timesheets;
    };

    // Calculate hours for a specific month from a timesheet
    const calculateHoursForMonth = (timesheet, targetMonthYear) => {
        let totalHours = 0;
        
        timesheet.timeSheetHours.forEach(day => {
            const dayMonthYear = moment(day.weekDate, 'YYYY-MM-DD').format('MMMM YYYY');
            if (dayMonthYear === targetMonthYear) {
                const dayTotal = day.projectHours.reduce((sum, project) => sum + project.hours, 0);
                totalHours += dayTotal;
            }
        });
        
        return totalHours;
    };

    const groupByMonth = (timesheets) => {
        // Group timesheets by month and year
        const grouped = timesheets.reduce((acc, timesheet) => {
            // Get all unique months in this timesheet
            const monthsInTimesheet = new Set();
            
            timesheet.timeSheetHours.forEach(day => {
                const monthYear = moment(day.weekDate, 'YYYY-MM-DD').format('MMMM YYYY');
                monthsInTimesheet.add(monthYear);
            });
            
            // Add timesheet to each month it spans
            monthsInTimesheet.forEach(monthYear => {
                if (!acc[monthYear]) {
                    acc[monthYear] = [];
                }
                acc[monthYear].push(timesheet);
            });
            
            return acc;
        }, {});

        // Get an array of months sorted in descending order
        const sortedMonths = Object.keys(grouped).sort((a, b) => {
            return moment(b, 'MMMM YYYY') - moment(a, 'MMMM YYYY'); // Sort in descending order
        });

        // Return sorted grouped timesheets
        const sortedGrouped = {};
        sortedMonths.forEach(month => {
            sortedGrouped[month] = grouped[month];
        });

        return sortedGrouped;
    };

    const renderTimesheetDetails = (timesheet) => {
        const dateHoursMap = {};
        const uniqueProjects = [];

        timesheet.timeSheetHours.forEach(timeSheetHour => {
            const formattedDate = timeSheetHour.weekDate;

            timeSheetHour.projectHours.forEach(projectHour => {
                if (!dateHoursMap[formattedDate]) {
                    dateHoursMap[formattedDate] = {};
                }
                if (!dateHoursMap[formattedDate][projectHour.projectName]) {
                    dateHoursMap[formattedDate][projectHour.projectName] = 0;
                }
                // Add project to uniqueProjects array if it doesn't exist
                if (!uniqueProjects.includes(projectHour.projectName)) {
                    uniqueProjects.push(projectHour.projectName);
                }
            });
        });

        // Update dateHoursMap with actual hours
        timesheet.timeSheetHours.forEach(timeSheetHour => {
            const formattedDate = timeSheetHour.weekDate;

            timeSheetHour.projectHours.forEach(projectHour => {
                dateHoursMap[formattedDate][projectHour.projectName] += projectHour.hours;
            });
        });

        // Adjust startDay to start from Monday (day 1)
        const startDay = moment(timesheet.weekStartDate, "YYYY-MM-DD").day(1).toDate();
        const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date(startDay);
            date.setDate(startDay.getDate() + i);
            const formattedDate = date.toISOString().split('T')[0];
            return formattedDate;
        });

        // Calculate total hours per day across all projects
        const dailyTotals = days.map(date => {
            const totalForDay = uniqueProjects.reduce((acc, projectName) => {
                return acc + ((dateHoursMap[date] && dateHoursMap[date][projectName]) || 0);
            }, 0);
            return totalForDay;
        });

        // Render table rows
        const projectRows = uniqueProjects.map((projectName, index) => {
            const daysData = days.map((date, i) => {
                const hoursForDate = (dateHoursMap[date] && dateHoursMap[date][projectName]) || 0;
                return (
                    <td key={i}>
                        {hoursForDate.toFixed(2)}
                    </td>
                );
            });

            return (
                <tr key={index}>
                    <td>{projectName}</td>
                    {daysData}
                    <th>
                        <div className="days-view-total-hours">
                            {timesheet.timeSheetHours
                                .filter(timeSheetHour => timeSheetHour.projectHours.some(projectHour => projectHour.projectName === projectName))
                                .reduce((acc, timeSheetHour) => acc + timeSheetHour.projectHours.reduce((acc, ph) => ph.projectName === projectName ? acc + ph.hours : acc, 0), 0).toFixed(2)
                            }
                        </div>
                    </th>
                </tr>
            );
        });

        // Add total row for daily hours
        const totalRow = (
            <tr key="total-row">
                <td><strong>Total</strong></td>
                {dailyTotals.map((total, i) => (
                    <td key={i}>
                        <strong>{total.toFixed(2)}</strong>
                    </td>
                ))}
                <th>
                    <div className="days-view-total-hours">
                        <strong>{dailyTotals.reduce((acc, total) => acc + total, 0).toFixed(2)}</strong>
                    </div>
                </th>
            </tr>
        );

        return [...projectRows, totalRow];
    };

    const getUniqueCommentsByDate = (timeSheetHours) => {
        const commentMap = new Map();
        timeSheetHours.forEach(day => {
            day.projectHours.forEach(ph => {
                if (ph.userComments) {
                    commentMap.set(day.weekDate, ph.userComments);
                }
            });
        });
        return Array.from(commentMap.entries()).map(([date, comment]) => ({
            date,
            comment
        })).sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    const groupedTimesheets = groupByMonth(getFilteredTimesheets());

    const sendEmail = (email) => {
        if (!timesheets.length) {
            toast.warning("No data available for export.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        const exportData = timesheets.map((timesheet) => {
            const uniqueComments = getUniqueCommentsByDate(timesheet.timeSheetHours);
            const comments = uniqueComments
                .map(({ date, comment }) => `${moment(date).format("DD-MM-YYYY")}: ${comment}`)
                .join("; ");
            return {
                "User ID": employeeId,
                "Week Number": timesheet.weekNumber,
                "Start & End Date": `${timesheet.weekStartDate} - ${timesheet.weekEndDate}`,
                "Total (Hours)": timesheet.totalHours,
                Status: timesheet.status,
                Notes: comments,
            };
        });
        const ws = utils.json_to_sheet(exportData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Timesheets');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        // Convert the Excel file to a Base64 string
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
            const base64data = reader.result.split(',')[1]; // Get the Base64 string without the prefix

            const link = document.createElement('a');
            link.href = reader.result;
            const today = new Date();
            const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
            const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
            const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
            const formattedDate = `${year}${month}${day}`;
            link.download = `WW360-View_Timesheet_Report_${formattedDate}.xlsx`;

            // Simulate a click to download the attachment
            link.click();

            // Prepare the mailto link (without attachment)
            const subject = "Timesheet List";
            const body = 'Please find the attached file.';
            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

            toast.info('The Excel file has been generated and downloaded. Please attach the file manually to your email.', {
                position: toast.POSITION.TOP_CENTER,
            });

            setTimeout(() => {
                window.location.href = mailtoLink;
            }, 3000);

        };
    };

    const handleExport = () => {
        // Ensure you have data in state before exporting
        if (!timesheets.length) {
            toast.warning("No data available for export.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        const exportData = timesheets.map((timesheet) => {
            const uniqueComments = getUniqueCommentsByDate(timesheet.timeSheetHours);
            const comments = uniqueComments
                .map(({ date, comment }) => `${moment(date).format("DD-MM-YYYY")}: ${comment}`)
                .join("; ");
            return {
                "User ID": employeeId,
                "Week Number": timesheet.weekNumber,
                "Start & End Date": `${timesheet.weekStartDate} - ${timesheet.weekEndDate}`,
                "Total (Hours)": timesheet.totalHours,
                Status: timesheet.status,
                Notes: comments,
            };
        });

        const ws = utils.json_to_sheet(exportData, {
            header: ["User ID", "Week Number", "Start & End Date", "Total (Hours)", "Status", "Notes"]
        });

        // Set column widths
        ws['!cols'] = [
            { wpx: 100 }, // User ID
            { wpx: 100 }, // Week Number
            { wpx: 150 }, // Start & End Date
            { wpx: 100 }, // Total (Hours)
            { wpx: 100 }, // Status
            { wpx: 300 }, // Notes
        ];

        // Apply bold font to headers only if cells exist
        if (exportData.length > 0) {
            ['A1', 'B1', 'C1', 'D1', 'E1', 'F1'].forEach(cell => {
                if (ws[cell]) {
                    ws[cell].s = { font: { bold: true } };
                }
            });
        }

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Timesheets');

        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        saveAs(data, `WW360-View_Timesheet_Report_${formattedDate}.xlsx`);
    };

    const shareData = {
        title: 'View Timesheet List',
        text: 'Check out the View Timesheet List',
        url: window.location.href
    };

    const handleShare = async () => {
        try {
            await navigator.share(shareData);
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const handleNotesClick = (timesheet) => {
        setSelectedTimesheet(timesheet);
        setShowNotesModal(true);
    };

    const closeNotesModal = () => {
        setShowNotesModal(false);
        setSelectedTimesheet(null);
    };

    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="row mb-2">
                                <div className="col">
                                    <h3 className="title mb-2">View Timesheet</h3>
                                </div>
                                <div className="col-auto">
                                    <a href={"/SubmitLeave"} className="btn btn-primary mt-lg-0 mt-4">
                                        <i className="ri-calendar-schedule-line"></i> Apply Leave
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="card mb-4">
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col">
                                            <h5 className="title mt-1 mb-0">Summary</h5>
                                        </div>
                                        <div className="col-auto">
                                            <button type="button" title="Email" className="btn btn-light btn-circle ml-2" onClick={() => sendEmail('')}>
                                                <i className="ri-mail-send-fill"></i>
                                            </button>
                                            <button type="button" title="Share" className="btn btn-light btn-circle ml-2" onClick={handleShare}>
                                                <i className="ri-share-fill"></i>
                                            </button>
                                            <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={handleExport}>
                                                <i className="ri-export-fill"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row align-items-center justify-content-center" id="filter-table" style={{ display: 'none' }}>
                                        <div className="col-lg-12 col-md-12 text-center">
                                            <div className="btn-group btn-group-toggle" data-toggle="buttons">
                                                <label className="btn btn-outline-primary active">
                                                    <input type="radio" name="filterBy" id="currentMonth" checked={filter === "currentMonth"} onChange={handleFilterChange} />Current Month
                                                </label>
                                                <label className="btn btn-outline-primary">
                                                    <input type="radio" name="filterBy" id="last3Months" checked={filter === "last3Months"} onChange={handleFilterChange} /> Last 3 Months
                                                </label>
                                                <label className="btn btn-outline-primary">
                                                    <input type="radio" name="filterBy" id="last6Months" checked={filter === "last6Months"} onChange={handleFilterChange} /> Last 6 Months
                                                </label>
                                                <label className="btn btn-outline-primary">
                                                    <input type="radio" name="filterBy" id="ytd" checked={filter === "ytd"} onChange={handleFilterChange} /> Year To Date
                                                </label>
                                                <label className="btn btn-outline-primary">
                                                    <input type="radio" name="filterBy" id="all" checked={filter === "all"} onChange={handleFilterChange} /> View All
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-md-12" id="timesheetDetailsSummary">
                                            {Object.keys(groupedTimesheets).length > 0 ? (
                                                Object.keys(groupedTimesheets).map((monthYear, index) => (
                                                    <div key={index}>
                                                        <h6 className="title accordion-title" data-toggle="collapse" data-target={`#monthDetails${index}`}>
                                                            <i className="ri-calendar-2-line"></i> {monthYear}
                                                            <span className="show-hours-details float-right">
                                                                <div className="row">
                                                                    <div className="col border-right"><i className="ri-time-line"></i> Hours</div>
                                                                    <div className="col-auto">
                                                                        <span className="text-success">
                                                                            <i className="ri-thumb-up-fill"></i>
                                                                            {groupedTimesheets[monthYear].reduce((total, timesheet) => 
                                                                                total + (timesheet.status?.toLowerCase() === 'approved' ? calculateHoursForMonth(timesheet, monthYear) : 0), 0).toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="col-auto">
                                                                        <span className="text-secondary">
                                                                            <i className="ri-thumb-up-fill"></i>
                                                                            {groupedTimesheets[monthYear].reduce((total, timesheet) => 
                                                                                total + (timesheet.status?.toLowerCase() === 'submitted' ? calculateHoursForMonth(timesheet, monthYear) : 0), 0).toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="col-auto">
                                                                        <span className="text-danger">
                                                                            <i className="ri-thumb-down-fill"></i>
                                                                            {groupedTimesheets[monthYear].reduce((total, timesheet) => 
                                                                                total + (timesheet.status?.toLowerCase() === 'rejected' ? calculateHoursForMonth(timesheet, monthYear) : 0), 0).toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </span>
                                                        </h6>
                                                        <div className="table-responsive timesheet-information collapse" id={`monthDetails${index}`} data-parent="#timesheetDetailsSummary">
                                                            <table className="table grid">
                                                                <thead className="bg-light">
                                                                    <tr>
                                                                        <th>Emp ID</th>
                                                                        <th>Emp Name</th>
                                                                        <th width="15%" className="text-center">Week Number</th>
                                                                        <th width="20%">Start & End Date</th>
                                                                        <th width="15%" className="text-center">Total <small>(Hours)</small></th>
                                                                        <th width="15%" className="text-center">Status</th>
                                                                        <th width="10%" className="text-center">Notes</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {groupedTimesheets[monthYear].map((timesheet, weekIndex) => (
                                                                        <React.Fragment key={weekIndex}>
                                                                            <tr data-toggle="collapse" data-target={`#weekDetails${index}-${weekIndex}`} className="accordion-row collapsed" title="More Details">
                                                                                <td><i className="ri-user-2-line"></i> {employeeId}</td>
                                                                                <td>{employeeName}</td>
                                                                                <td className="text-center"><span className="text-info border badge border-info">{timesheet.weekNumber}</span></td>
                                                                                <td>{moment(timesheet.weekStartDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - {moment(timesheet.weekEndDate, "YYYY-MM-DD").format("DD-MM-YYYY")}</td>
                                                                                <td className="text-center"><span className="text-secondary">{calculateHoursForMonth(timesheet, monthYear).toFixed(2)}</span></td>
                                                                                <td className="text-center">
                                                                                    <span className={`badge border status ${timesheet.status?.toLowerCase() === 'approved' ? 'border-success text-success' : timesheet.status === 'Rejected' ? 'border-danger text-danger' : 'border-secondary text-secondary'}`}>
                                                                                        {timesheet.status?.toLowerCase() === 'rejected' ? (
                                                                                            <i className="ri-thumb-down-fill"></i>
                                                                                        ) : (
                                                                                            <i className="ri-thumb-up-fill"></i>
                                                                                        )}
                                                                                        {timesheet.status}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <button
                                                                                        type="button"
                                                                                        title="View Notes"
                                                                                        className="btn btn-light btn-circle"
                                                                                        onClick={() => handleNotesClick(timesheet)}
                                                                                    >
                                                                                        <i className="ri-sticky-note-fill"></i>
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                            <tr className="accordion">
                                                                                <td colSpan="7" className="px-0 collapse" id={`weekDetails${index}-${weekIndex}`} data-parent={`#monthDetails${index}`}>
                                                                                    <div className="accordion-content days-view section-submitted">
                                                                                        <table className="table grid">
                                                                                            <thead className="bg-light">
                                                                                                <tr>
                                                                                                    <th>Activity</th>
                                                                                                    <th>Sun <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(0).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Mon <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(1).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Tue <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(2).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Wed <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(3).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Thu <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(4).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Fri <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(5).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Sat <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(6).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Total <small>(Hours)</small></th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {renderTimesheetDetails(timesheet)}
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        </React.Fragment>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        {index < Object.keys(groupedTimesheets).length - 1 && <hr />}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="col">
                                                    <div className="no-data-found">
                                                        <i className="ri-calendar-fill"></i>
                                                        <div className="bounce ripple"><i className="ri-checkbox-circle-fill"></i></div>
                                                        <h6>No Timesheets Available</h6>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showNotesModal && selectedTimesheet && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Notes for {moment(selectedTimesheet.weekStartDate, "YYYY-MM-DD").format("DD-MM-YYYY")} to {moment(selectedTimesheet.weekEndDate, "YYYY-MM-DD").format("DD-MM-YYYY")}</h5>
                                <button type="button" className="close" onClick={closeNotesModal}>
                                    <span>×</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                {selectedTimesheet.timeSheetHours.some(day => day.projectHours.some(ph => ph.userComments)) ? (
                                    <ul className="list-group">
                                        {getUniqueCommentsByDate(selectedTimesheet.timeSheetHours).map((comment, idx) => (
                                            <li key={idx} className="list-group-item text-left">
                                                <strong>{moment(comment.date).format("DD-MM-YYYY")}</strong> - {comment.comment}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-center">No comments available for this timesheet.</p>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeNotesModal}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewTimesheet;